import Papa from 'papaparse';
import saveAs from 'file-saver';
import { safeDivide } from 'src/utils/app';

// Define interfaces for function parameters
export interface ChartExportRow {
  [key: string]: string | number;
}

export interface StackedChartDataSet {
  name: string;
  data: { y: number; rawValue?: number; color: string }[];
  color: string;
  totalVolume?: number;
}

export interface GenerateChartExportDataParams {
  chartSeries: Array<{ type: string; data: StackedChartDataSet[]; metricType: any; groupBy?: string }>;
  chartCategories: string[];
  groupingType: 'keyword' | 'category' | 'subcategory' | 'topic';
  totalEntityTrafficMap: Map<string, number>;
  groupedBrandData: Array<{
    brandId: number;
    brandName: string;
    keywordOrder: string[];
    keywordVolume: number[];
  }> | null;
  getEntityDisplayName: (fieldKey: string | number) => string;
}

/**
 * Generates export data for the traffic chart based on grouping type
 */
export const generateStackedChartExportData = ({
  chartSeries,
  chartCategories,
  groupingType,
  totalEntityTrafficMap,
  groupedBrandData,
  getEntityDisplayName
}: GenerateChartExportDataParams) => {
  if (!chartSeries.length || !chartSeries[0].data || !chartCategories.length) {
    return { headers: [], rows: [] };
  }

  const totalTrafficMap = totalEntityTrafficMap;

  // Series data (including Other Brands)
  const seriesData = chartSeries[0].data;

  // Entity keys (fieldKeys)
  const entityKeys = groupedBrandData?.[0]?.keywordOrder || [];

  const rows: ChartExportRow[] = [];

  entityKeys.forEach((entityKey, idx) => {
    const entityDisplayName = groupingType === 'keyword' ? entityKey : getEntityDisplayName(entityKey);
    const totalTraffic = totalTrafficMap.get(entityKey as string) || 0;

    // Calculate the sum of traffic from all selected brands for this entity
    let selectedBrandsSum = 0;
    seriesData.forEach((brandSeries) => {
      // Skip the "Other Brands" series when summing selected brands
      if (brandSeries.name !== 'Other Brands') {
        selectedBrandsSum += brandSeries.data[idx]?.rawValue || 0;
      }
    });

    // Calculate the "Other Brands" traffic for this entity
    const otherBrandsTraffic = Math.max(0, totalTraffic - selectedBrandsSum);

    // Process each brand's data for this entity (including 'Other Brands')
    seriesData.forEach((brandSeries) => {
      const brandName = brandSeries.name;
      // If this is the "Other Brands" series, use our calculated value
      const value = brandName === 'Other Brands' ? otherBrandsTraffic : brandSeries.data[idx]?.rawValue || 0;

      // Skip adding rows for brands with zero traffic
      if (value <= 0) return;

      const percentage = safeDivide(value, totalTraffic) * 100;

      const row: ChartExportRow = {};

      // Set columns based on groupingType
      if (groupingType === 'keyword') {
        row.Keyword = entityDisplayName;
        row.Brand = brandName;
        row.Total_traffic_per_brand = value;
        row.Total_traffic_keyword = totalTraffic;
        row['% of_total_traffic'] = percentage;
      } else if (groupingType === 'category') {
        row.Category = entityDisplayName;
        row.CategoryId = entityKey;
        row.Brand = brandName;
        row.Total_traffic_per_brand = value;
        row.Total_traffic_category = totalTraffic;
        row['% of_total_traffic'] = percentage;
      } else if (groupingType === 'subcategory') {
        row.Subcategory = entityDisplayName;
        row.SubcategoryId = entityKey;
        row.Brand = brandName;
        row.Total_traffic_per_brand = value;
        row.Total_traffic_subcategory = totalTraffic;
        row['% of_total_traffic'] = percentage;
      }

      rows.push(row);
    });

    // If "Other Brands" is not already in the series data but we have other brands traffic,
    // add it explicitly
    const hasOtherBrandsSeries = seriesData.some((series) => series.name === 'Other Brands');
    if (!hasOtherBrandsSeries && otherBrandsTraffic > 0) {
      const percentage = safeDivide(otherBrandsTraffic, totalTraffic) * 100;
      const row: ChartExportRow = {};

      if (groupingType === 'keyword') {
        row.Keyword = entityDisplayName;
        row.Brand = 'Other Brands';
        row.Total_traffic_per_brand = otherBrandsTraffic;
        row.Total_traffic_keyword = totalTraffic;
        row['% of_total_traffic'] = percentage;
      } else if (groupingType === 'category') {
        row.Category = entityDisplayName;
        row.CategoryId = entityKey;
        row.Brand = 'Other Brands';
        row.Total_traffic_per_brand = otherBrandsTraffic;
        row.Total_traffic_category = totalTraffic;
        row['% of_total_traffic'] = percentage;
      } else if (groupingType === 'subcategory') {
        row.Subcategory = entityDisplayName;
        row.subcategoryId = entityKey;
        row.Brand = 'Other Brands';
        row.Total_traffic_per_brand = otherBrandsTraffic;
        row.Total_traffic_subcategory = totalTraffic;
        row['% of_total_traffic'] = percentage;
      }

      rows.push(row);
    }
  });

  // Generate headers based on the groupingType
  let headers: string[] = [];
  if (groupingType === 'keyword') {
    headers = ['Keyword', 'Brand', 'Total_traffic_per_brand', 'Total_traffic_keyword', '% of_total_traffic'];
  } else if (groupingType === 'category') {
    headers = [
      'Category',
      'CategoryId',
      'Brand',
      'Total_traffic_per_brand',
      'Total_traffic_category',
      '% of_total_traffic'
    ];
  } else if (groupingType === 'subcategory') {
    headers = [
      'Subcategory',
      'SubcategoryId',
      'Brand',
      'Total_traffic_per_brand',
      'Total_traffic_subcategory',
      '% of_total_traffic'
    ];
  }

  return { headers, rows };
};

/**
 * Exports chart data to a CSV file
 */
export const exportChartDataToCsv = (
  headers: string[],
  rows: ChartExportRow[],
  groupingType: 'keyword' | 'category' | 'subcategory' | 'topic',
  dateDisplayName: string
) => {
  if (rows.length === 0) {
    console.error('No data to export');
    return false;
  }

  // Generate filename based on groupingType
  const filename = `${groupingType}_traffic_by_brand_${dateDisplayName}.csv`;

  try {
    const csvContent = Papa.unparse(
      {
        fields: headers,
        data: rows as any
      },
      {
        delimiter: ',',
        header: true,
        skipEmptyLines: true
      }
    );

    const csvWithBOM = `\ufeff${csvContent}`;
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8' });
    saveAs(blob, filename);
    return true;
  } catch (error) {
    console.error('Error generating CSV:', error);
    return false;
  }
};
